import { Component, ViewChild, OnInit } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ApiService } from 'src/app/shared/services/api.service';
import { Router } from '@angular/router';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';
import { RbacService } from 'src/app/shared/services/rbac.service';

export interface Role {
  role_id: number;
  id?: number;
  role_name: string;
  description: string;
  permissions: any;
  user_type: number;
  org_id: number | null;
  created_at: string;
  updated_at?: string;
}

@Component({
  selector: 'app-role-list',
  templateUrl: './role-list.component.html',
  styleUrls: ['./role-list.component.css'],
})
export class RoleListComponent implements OnInit {
  displayedColumns: string[] = [
    'index',
    'role_name',
    'description',
    'permissions_summary',
    'created_at',
    'actions',
  ];
  dataSource = new MatTableDataSource<Role>([]);
  isLoading = false;
  canEdit = false;
  canDelete = false;

  /** Paginator/sort live inside `*ngIf`; use setters so `MatTableDataSource` connects after they render. */
  paginator?: MatPaginator;

  @ViewChild(MatPaginator)
  set paginatorRef(pg: MatPaginator | undefined) {
    this.paginator = pg;
    this.dataSource.paginator = pg ?? null;
  }

  @ViewChild(MatSort)
  set sortRef(s: MatSort | undefined) {
    this.dataSource.sort = s ?? null;
  }

  constructor(
    private apiService: ApiService,
    private toastrMsgService: ToastrMsgService,
    private rbacService: RbacService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.canEdit = this.rbacService.hasPermission('admin', 'update');
    this.canDelete = this.rbacService.hasPermission('admin', 'delete');

    this.dataSource.filterPredicate = (data: Role, filter: string) => {
      const searchStr = filter.toLowerCase();
      const roleName = data.role_name?.toLowerCase() || '';
      const description = data.description?.toLowerCase() || '';
      const permissionsSummary = this.getPermissionsSummary(data)?.toLowerCase() || '';
      return (
        roleName.includes(searchStr) ||
        description.includes(searchStr) ||
        permissionsSummary.includes(searchStr)
      );
    };

    this.dataSource.sortingDataAccessor = (item: Role, property: string) => {
      switch (property) {
        case 'created_at':
          return item.created_at ? new Date(item.created_at).getTime() : 0;
        default:
          return (item as any)[property] ?? '';
      }
    };

    this.fetchData();
  }

  /** 1-based row index for the current page (replaces non-sequential `role_id` in the # column). */
  displayRowNumber(rowIndexOnPage: number): number {
    if (!this.paginator) {
      return rowIndexOnPage + 1;
    }
    return this.paginator.pageIndex * this.paginator.pageSize + rowIndexOnPage + 1;
  }

  get rangeStart(): number {
    if (!this.paginator || !this.dataSource.filteredData.length) {
      return 0;
    }
    return this.paginator.pageIndex * this.paginator.pageSize + 1;
  }

  get rangeEnd(): number {
    if (!this.paginator) {
      return 0;
    }
    const end = (this.paginator.pageIndex + 1) * this.paginator.pageSize;
    return Math.min(end, this.dataSource.filteredData.length);
  }

  permissionBadgeClass(role: Role): string {
    const n = this.getPermissionCount(role);
    if (n === 0) {
      return 'bg-gray-100 text-gray-700 border-gray-200';
    }
    if (n < 10) {
      return 'bg-amber-50 text-amber-900 border-amber-200';
    }
    return 'bg-blue-50 text-blue-800 border-blue-200';
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value.trim().toLowerCase();
    this.dataSource.filter = filterValue;
    if (this.paginator) {
      this.paginator.firstPage();
    }
  }

  fetchData(): void {
    this.isLoading = true;
    this.apiService.getAllRoles().subscribe({
      next: (res: any) => {
        const roles = Array.isArray(res)
          ? res.map((role: any) => ({
              ...role,
              role_id: role.role_id || role.id,
            }))
          : [];
        this.dataSource.data = roles;
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error occurred:', err);
        this.toastrMsgService.error(err.error?.message || 'Failed to load roles');
        this.isLoading = false;
      },
      complete: () => {
        this.isLoading = false;
      },
    });
  }

  getPermissionsSummary(role: Role): string {
    if (!role.permissions) return 'No permissions';

    try {
      const perms =
        typeof role.permissions === 'string' ? JSON.parse(role.permissions) : role.permissions;

      if (Array.isArray(perms)) {
        const enabledModules = perms
          .filter((p: any) => p.view || p.add || p.update || p.delete)
          .map((p: any) => p.module)
          .join(', ');
        return enabledModules || 'No permissions';
      }

      if (typeof perms === 'object') {
        const modules = Object.keys(perms).filter(
          (module) =>
            perms[module]?.view ||
            perms[module]?.add ||
            perms[module]?.update ||
            perms[module]?.delete
        );
        return modules.join(', ') || 'No permissions';
      }

      return 'No permissions';
    } catch (e) {
      console.error('Error parsing permissions:', e);
      return 'Invalid permissions';
    }
  }

  getPermissionCount(role: Role): number {
    if (!role.permissions) return 0;

    try {
      const perms =
        typeof role.permissions === 'string' ? JSON.parse(role.permissions) : role.permissions;

      if (Array.isArray(perms)) {
        return perms.filter((p: any) => p.view || p.add || p.update || p.delete).length;
      }

      if (typeof perms === 'object') {
        return Object.keys(perms).filter(
          (module) =>
            perms[module]?.view ||
            perms[module]?.add ||
            perms[module]?.update ||
            perms[module]?.delete
        ).length;
      }

      return 0;
    } catch (e) {
      return 0;
    }
  }

  editRole(role: Role): void {
    if (!this.canEdit) {
      this.toastrMsgService.error('You do not have permission to edit roles');
      return;
    }
    const roleId = role.role_id || role.id;
    this.router.navigate(['/home/edit-role-and-permission', roleId]);
  }

  deleteRole(id: number): void {
    if (!this.canDelete) {
      this.toastrMsgService.error('You do not have permission to delete roles');
      return;
    }

    if (!confirm('Are you sure you want to delete this role? This action cannot be undone.')) {
      return;
    }

    this.apiService.deleteRole(id).subscribe({
      next: () => {
        this.toastrMsgService.success('Role deleted successfully');
        this.fetchData();
        this.rbacService.refreshPermissions();
      },
      error: (err) => {
        this.toastrMsgService.error(
          err.error?.errorMessage || err.error?.message || 'Failed to delete role'
        );
      },
    });
  }

  viewRoleDetails(role: Role): void {
    console.log('Role details:', role);
  }
}
